import React, { useState, useEffect } from 'react';
import { adminAPI, ReportsAnalytics as ReportsAnalyticsType } from '../../services/api';
import './ReportsAnalytics.css';

const ReportsAnalytics: React.FC = () => {
  const [reportsData, setReportsData] = useState<ReportsAnalyticsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'overview' | 'loans' | 'users' | 'financial' | 'risk' | 'trends'>('overview');
  const [isExporting, setIsExporting] = useState(false);

  // Fetch reports data from API
  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);
        const response = await adminAPI.getReportsAnalytics();
        
        if (response.success) {
          setReportsData(response.data);
        } else {
          setError(response.message || 'Failed to fetch reports data');
        }
      } catch (err) {
        setError('Failed to fetch reports data');
        console.error('Error fetching reports:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, []);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR'
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${(value * 100).toFixed(1)}%`;
  };

  const handleExport = async (type: 'loans' | 'users' | 'payments' | 'financial', format: 'csv' | 'excel' | 'pdf') => {
    try {
      setIsExporting(true);
      const response = await adminAPI.exportReportsData({
        format,
        type,
        dateRange: 'last_30_days'
      });

      if (response.success) {
        // In a real app, this would trigger a download
        alert(`Export successful! File: ${response.data.filename}`);
      } else {
        setError(response.message || 'Failed to export data');
      }
    } catch (err) {
      setError('Failed to export data');
      console.error('Error exporting data:', err);
    } finally {
      setIsExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="reports-analytics">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading reports and analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reports-analytics">
        <div className="error-state">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  if (!reportsData) {
    return (
      <div className="reports-analytics">
        <div className="error-state">
          <p>No reports data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="reports-analytics">
      {/* Header */}
      <div className="reports-header">
        <h2 className="reports-title">Reports & Analytics</h2>
        <p className="reports-subtitle">
          Comprehensive insights into loan performance, user behavior, and financial metrics
        </p>
      </div>

      {/* Export Controls */}
      <div className="export-controls">
        <div className="export-section">
          <h3>Export Reports</h3>
          <div className="export-buttons">
            <div className="export-group">
              <label>Data Type:</label>
              <select className="export-select" defaultValue="loans">
                <option value="loans">Loans</option>
                <option value="users">Users</option>
                <option value="payments">Payments</option>
                <option value="financial">Financial</option>
              </select>
            </div>
            <div className="export-group">
              <label>Format:</label>
              <select className="export-select" defaultValue="csv">
                <option value="csv">CSV</option>
                <option value="excel">Excel</option>
                <option value="pdf">PDF</option>
              </select>
            </div>
            <button
              className="export-btn"
              onClick={() => handleExport('loans', 'csv')}
              disabled={isExporting}
            >
              {isExporting ? 'Exporting...' : 'Export'}
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="reports-tabs">
        <button
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab-btn ${activeTab === 'loans' ? 'active' : ''}`}
          onClick={() => setActiveTab('loans')}
        >
          Loan Metrics
        </button>
        <button
          className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          User Analytics
        </button>
        <button
          className={`tab-btn ${activeTab === 'financial' ? 'active' : ''}`}
          onClick={() => setActiveTab('financial')}
        >
          Financial Metrics
        </button>
        <button
          className={`tab-btn ${activeTab === 'risk' ? 'active' : ''}`}
          onClick={() => setActiveTab('risk')}
        >
          Risk Analysis
        </button>
        <button
          className={`tab-btn ${activeTab === 'trends' ? 'active' : ''}`}
          onClick={() => setActiveTab('trends')}
        >
          Trends
        </button>
      </div>

      {/* Tab Content */}
      <div className="reports-content">
        {activeTab === 'overview' && (
          <div className="overview-tab">
            <div className="overview-grid">
              <div className="metric-card">
                <div className="metric-icon revenue">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2l10 5-10 5L2 7l10-5z"/>
                    <path d="M2 17l10 5 10-5"/>
                    <path d="M2 12l10 5 10-5"/>
                  </svg>
                </div>
                <div className="metric-content">
                  <h3>Total Revenue</h3>
                  <p className="metric-value">{formatCurrency(reportsData.overview.totalRevenue)}</p>
                  <span className="metric-change positive">+{formatPercentage(reportsData.overview.monthlyGrowth)}</span>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon loans">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14,2 14,8 20,8"/>
                  </svg>
                </div>
                <div className="metric-content">
                  <h3>Total Loans</h3>
                  <p className="metric-value">{reportsData.overview.totalLoans}</p>
                  <span className="metric-change positive">+{formatPercentage(reportsData.overview.quarterlyGrowth)}</span>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon repayment">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22,4 12,14.01 9,11.01"/>
                  </svg>
                </div>
                <div className="metric-content">
                  <h3>Repayment Rate</h3>
                  <p className="metric-value">{formatPercentage(reportsData.overview.repaymentRate)}</p>
                  <span className="metric-change positive">Excellent</span>
                </div>
              </div>

              <div className="metric-card">
                <div className="metric-icon average">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <path d="M12 6v6l4 2"/>
                  </svg>
                </div>
                <div className="metric-content">
                  <h3>Average Loan</h3>
                  <p className="metric-value">{formatCurrency(reportsData.overview.averageLoanAmount)}</p>
                  <span className="metric-change neutral">Stable</span>
                </div>
              </div>
            </div>

            <div className="overview-charts">
              <div className="chart-card">
                <h3>Monthly Revenue Trend</h3>
                <div className="chart-placeholder">
                  <div className="chart-bars">
                    {reportsData.financialMetrics.monthlyRevenue.slice(-6).map((month, index) => (
                      <div key={index} className="chart-bar">
                        <div 
                          className="bar-fill"
                          style={{ height: `${(month.revenue / 20000) * 100}%` }}
                        ></div>
                        <span className="bar-label">{month.month.split(' ')[0]}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="chart-card">
                <h3>Loan Distribution by Amount</h3>
                <div className="chart-placeholder">
                  <div className="pie-chart">
                    {reportsData.loanMetrics.loansByAmount.map((item, index) => (
                      <div key={index} className="pie-segment">
                        <div 
                          className="segment-color"
                          style={{ 
                            backgroundColor: ['#0074D9', '#2ECC40', '#F97316'][index],
                            width: `${item.percentage}%`
                          }}
                        ></div>
                        <span className="segment-label">{item.range}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'loans' && (
          <div className="loans-tab">
            <div className="loans-metrics">
              <div className="metric-row">
                <div className="metric-item">
                  <span className="metric-label">Total Disbursed</span>
                  <span className="metric-value">{formatCurrency(reportsData.loanMetrics.totalDisbursed)}</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Total Repaid</span>
                  <span className="metric-value">{formatCurrency(reportsData.loanMetrics.totalRepaid)}</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Outstanding Balance</span>
                  <span className="metric-value">{formatCurrency(reportsData.loanMetrics.outstandingBalance)}</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Avg Repayment Time</span>
                  <span className="metric-value">{reportsData.loanMetrics.averageRepaymentTime} days</span>
                </div>
              </div>
            </div>

            <div className="loans-tables">
              <div className="table-card">
                <h3>Loans by Institution</h3>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Institution</th>
                      <th>Count</th>
                      <th>Amount</th>
                      <th>Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportsData.loanMetrics.loansByInstitution.map((item, index) => (
                      <tr key={index}>
                        <td>{item.institution}</td>
                        <td>{item.count}</td>
                        <td>{formatCurrency(item.amount)}</td>
                        <td>{((item.count / reportsData.overview.totalLoans) * 100).toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="table-card">
                <h3>Loans by Amount Range</h3>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Range</th>
                      <th>Count</th>
                      <th>Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportsData.loanMetrics.loansByAmount.map((item, index) => (
                      <tr key={index}>
                        <td>{item.range}</td>
                        <td>{item.count}</td>
                        <td>{item.percentage}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="users-tab">
            <div className="users-metrics">
              <div className="metric-row">
                <div className="metric-item">
                  <span className="metric-label">Total Users</span>
                  <span className="metric-value">{reportsData.userMetrics.totalUsers}</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Active Users</span>
                  <span className="metric-value">{reportsData.userMetrics.activeUsers}</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">New This Month</span>
                  <span className="metric-value">{reportsData.userMetrics.newUsersThisMonth}</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Retention Rate</span>
                  <span className="metric-value">{formatPercentage(reportsData.userMetrics.userRetentionRate)}</span>
                </div>
              </div>
            </div>

            <div className="users-table">
              <div className="table-card">
                <h3>Users by Institution</h3>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Institution</th>
                      <th>User Count</th>
                      <th>Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportsData.userMetrics.usersByInstitution.map((item, index) => (
                      <tr key={index}>
                        <td>{item.institution}</td>
                        <td>{item.count}</td>
                        <td>{((item.count / reportsData.userMetrics.totalUsers) * 100).toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'financial' && (
          <div className="financial-tab">
            <div className="financial-metrics">
              <div className="metric-row">
                <div className="metric-item">
                  <span className="metric-label">Profit Margin</span>
                  <span className="metric-value">{formatPercentage(reportsData.financialMetrics.profitMargin)}</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Operational Costs</span>
                  <span className="metric-value">{formatCurrency(reportsData.financialMetrics.operationalCosts)}</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Net Profit</span>
                  <span className="metric-value">{formatCurrency(reportsData.financialMetrics.netProfit)}</span>
                </div>
              </div>
            </div>

            <div className="financial-chart">
              <div className="chart-card">
                <h3>Monthly Revenue & Loan Volume</h3>
                <div className="chart-placeholder">
                  <div className="dual-chart">
                    <div className="chart-bars">
                      {reportsData.financialMetrics.monthlyRevenue.slice(-6).map((month, index) => (
                        <div key={index} className="chart-bar">
                          <div 
                            className="bar-fill revenue"
                            style={{ height: `${(month.revenue / 20000) * 100}%` }}
                          ></div>
                          <div 
                            className="bar-fill loans"
                            style={{ height: `${(month.loans / 30) * 100}%` }}
                          ></div>
                          <span className="bar-label">{month.month.split(' ')[0]}</span>
                        </div>
                      ))}
                    </div>
                    <div className="chart-legend">
                      <div className="legend-item">
                        <div className="legend-color revenue"></div>
                        <span>Revenue</span>
                      </div>
                      <div className="legend-item">
                        <div className="legend-color loans"></div>
                        <span>Loans</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'risk' && (
          <div className="risk-tab">
            <div className="risk-metrics">
              <div className="metric-row">
                <div className="metric-item">
                  <span className="metric-label">Average Risk Score</span>
                  <span className="metric-value">{reportsData.riskMetrics.averageRiskScore}</span>
                </div>
                <div className="metric-item">
                  <span className="metric-label">Default Rate</span>
                  <span className="metric-value">{formatPercentage(reportsData.overview.defaultRate)}</span>
                </div>
              </div>
            </div>

            <div className="risk-tables">
              <div className="table-card">
                <h3>Risk Distribution</h3>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Risk Level</th>
                      <th>Count</th>
                      <th>Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportsData.riskMetrics.riskDistribution.map((item, index) => (
                      <tr key={index}>
                        <td>{item.range}</td>
                        <td>{item.count}</td>
                        <td>{item.percentage}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="table-card">
                <h3>Default Rate by Risk Level</h3>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Risk Level</th>
                      <th>Default Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportsData.riskMetrics.defaultByRisk.map((item, index) => (
                      <tr key={index}>
                        <td>{item.riskLevel}</td>
                        <td>{formatPercentage(item.defaultRate)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'trends' && (
          <div className="trends-tab">
            <div className="trends-charts">
              <div className="chart-card">
                <h3>Application Trends (Last 4 Weeks)</h3>
                <div className="chart-placeholder">
                  <div className="chart-bars">
                    {reportsData.trends.applicationTrends.map((trend, index) => (
                      <div key={index} className="chart-bar">
                        <div 
                          className="bar-fill applications"
                          style={{ height: `${(trend.applications / 25) * 100}%` }}
                        ></div>
                        <div 
                          className="bar-fill approvals"
                          style={{ height: `${(trend.approvals / 25) * 100}%` }}
                        ></div>
                        <span className="bar-label">{trend.period}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="chart-card">
                <h3>Seasonal Patterns</h3>
                <div className="seasonal-info">
                  <div className="seasonal-item">
                    <h4>Peak Months</h4>
                    <p>{reportsData.trends.seasonalPatterns.peakMonths.join(', ')}</p>
                  </div>
                  <div className="seasonal-item">
                    <h4>Low Activity Months</h4>
                    <p>{reportsData.trends.seasonalPatterns.lowMonths.join(', ')}</p>
                  </div>
                  <div className="seasonal-item">
                    <h4>Average Monthly Applications</h4>
                    <p>{reportsData.trends.seasonalPatterns.averageMonthlyApplications}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsAnalytics;





